import { Avatar, Badge, Button, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/config/constants';
import { notificationsApi, type AppNotification } from '@/services/notifications.service';

const PAGE_TITLES: Record<string, string> = {
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.ORDERS]: 'Orders',
  [ROUTES.RESTAURANTS]: 'Restaurants',
  [ROUTES.DELIVERY_PARTNERS]: 'Delivery Partners',
  [ROUTES.NEW_DELIVERY_PARTNER]: 'New Delivery Partner',
  [ROUTES.CUSTOMERS]: 'Customers',
  [ROUTES.SUBSCRIPTIONS]: 'Subscriptions',
  [ROUTES.SUBSCRIPTION_PLANS]: 'Manage plans',
  [ROUTES.NEW_SUBSCRIPTION_PLAN]: 'Create New Plan',
  [ROUTES.WALLET_FINANCE]: 'Wallet & Payouts',
  [ROUTES.PROMOTIONS]: 'Coupon Management',
  [ROUTES.NEW_COUPON]: 'Create Coupon',
  [ROUTES.REPORTS]: 'Reports',
  [ROUTES.SUPPORT_TICKETS]: 'Support / Tickets',
  [ROUTES.SERVICE_ZONES]: 'Service Zones',
  [ROUTES.NEW_SERVICE_CITY]: 'Add City',
  [ROUTES.NEW_SERVICE_ZONE]: 'Create Zone',
  [ROUTES.ADMINS]: 'Admins',
  [ROUTES.NEW_ROLE]: 'Create New Role',
  [ROUTES.STAFF]: 'Staff Management',
  [ROUTES.INVITE_STAFF]: 'Invite Staff',
  [ROUTES.BANNERS_CATEGORIES]: 'Category Management',
  [ROUTES.NEW_FOOD_CATEGORY]: 'Category Management',
  [ROUTES.MOBILE_BANNERS]: 'Banners Management',
  [ROUTES.NEW_MOBILE_BANNER]: 'Banners Management',
  [ROUTES.NOTIFICATIONS]: 'Notifications',
  [ROUTES.NEW_NOTIFICATION]: 'Send notification',
  [ROUTES.SETTINGS]: 'Settings',
  [ROUTES.INVOICE_GST]: 'Invoice & GST',
  [ROUTES.OPS_ADMIN]: 'Ops Admin Panel',
};

interface AppHeaderProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onMobileMenuOpen: () => void;
}

export default function AppHeader({ collapsed, onToggleCollapse, onMobileMenuOpen }: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [recentNotifications, setRecentNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    let cancelled = false;
    notificationsApi
      .list({ page: 1, pageSize: 5 })
      .then((data) => {
        if (!cancelled) setRecentNotifications(data.notifications);
      })
      .catch(() => {
        if (!cancelled) setRecentNotifications([]);
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const pageTitle = (() => {
    if (PAGE_TITLES[location.pathname]) return PAGE_TITLES[location.pathname];
    if (/\/invoice-gst\/\d+/.test(location.pathname)) return 'Invoice details';
    if (/\/support-tickets\/\d+/.test(location.pathname)) return 'Ticket details';
    if (/\/subscriptions\/plans\/\d+\/edit/.test(location.pathname)) return 'Edit Plan';
    if (/\/promotions\/\d+\/edit/.test(location.pathname)) return 'Edit Coupon';
    if (/\/delivery-partners\/\d+\/review/.test(location.pathname)) return 'Review Partner Documents';
    if (/\/delivery-partners\/\d+\/enabled/.test(location.pathname)) return 'Partner Enabled';
    if (/\/delivery-partners\/\d+/.test(location.pathname)) return 'Partner Profile';
    if (/\/customers\/\d+\/orders\/\d+/.test(location.pathname)) return 'Order Details';
    if (/\/customers\/\d+/.test(location.pathname)) return 'User Profile';
    if (/\/service-zones\/\d+\/edit/.test(location.pathname)) return 'Edit Zone';
    if (/\/service-zones\/\d+/.test(location.pathname)) return 'Zone Details';
    if (/\/admins\/roles\/\d+\/permissions/.test(location.pathname)) return 'Role Permissions';
    if (/\/admins\/roles\/\d+\/edit/.test(location.pathname)) return 'Edit Role';
    if (/\/admins\/staff\/\d+\/edit/.test(location.pathname)) return 'Edit Staff';
    if (/\/admins\/staff\/\d+/.test(location.pathname)) return 'Staff Profile';
    if (/\/banners-categories\/categories\/\d+\/edit/.test(location.pathname)) return 'Category Management';
    if (/\/banners-categories\/banners\/\d+/.test(location.pathname)) return 'Banners Management';
    return 'Admin';
  })();

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'My Profile' },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate(ROUTES.SETTINGS),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Log Out',
      danger: true,
      onClick: () => { logout(); navigate(ROUTES.LOGIN); },
    },
  ];

  const notificationItems: MenuProps['items'] = [
    ...(recentNotifications.length
      ? recentNotifications.map((item) => ({
          key: String(item.id),
          label: (
            <div className="py-1">
              <p className="font-semibold text-sm text-gray-800">{item.title}</p>
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.message}</p>
            </div>
          ),
        }))
      : [
          {
            key: 'empty',
            disabled: true,
            label: <p className="text-sm text-gray-500 m-0 py-1">No notifications yet</p>,
          },
        ]),
    { type: 'divider' },
    {
      key: 'all',
      label: <p className="text-center text-sm font-semibold" style={{ color: '#1677ff' }}>View all notifications</p>,
      onClick: () => navigate(ROUTES.NOTIFICATIONS),
    },
  ];

  return (
    <header
      className="sticky top-0 z-40 flex items-center justify-between shrink-0"
      style={{
        height: 68,
        paddingLeft: 16,
        paddingRight: 24,
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(15,23,42,0.06)',
      }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Button type="text" icon={<MenuOutlined />} onClick={onMobileMenuOpen}
          className="lg:hidden" size="large" />
        <Button type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggleCollapse}
          className="hidden lg:flex" size="large" />

        <div className="min-w-0 ml-1 flex flex-col justify-center">
          <h1 className="text-[17px] font-extrabold text-slate-900 leading-5 tracking-tight truncate">{pageTitle}</h1>
          <p className="text-xs text-slate-400 leading-4 mt-0.5 hidden sm:block">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <Dropdown menu={{ items: notificationItems }} trigger={['click']}
          placement="bottomRight" overlayStyle={{ width: 300 }}>
          <Button
            type="text"
            className="!w-10 !h-10 !min-w-10 !p-0 !rounded-xl hover:!bg-slate-100 !inline-flex !items-center !justify-center"
          >
            <Badge count={recentNotifications.length} size="small" offset={[2, 0]}>
              <span className="inline-flex h-5 w-5 items-center justify-center">
                <BellOutlined style={{ fontSize: 18, color: '#64748b' }} />
              </span>
            </Badge>
          </Button>
        </Dropdown>

        <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
          <button className="inline-flex items-center h-10 gap-2 pl-1 pr-2.5 rounded-2xl hover:bg-slate-100/80 transition-colors cursor-pointer border border-slate-200/80 bg-white">
            <Avatar size={28} icon={<UserOutlined />}
              style={{ background: 'linear-gradient(135deg, #1677ff 0%, #60a5fa 100%)', flexShrink: 0 }} />
            <div className="hidden sm:flex flex-col justify-center text-left leading-none whitespace-nowrap">
              <span className="text-sm font-semibold text-slate-800 leading-4">{user?.name ?? 'Admin'}</span>
              <span className="text-[11px] text-slate-400 leading-4 mt-0.5 capitalize">
                {user?.role?.replace(/_/g, ' ') ?? 'Super Admin'}
              </span>
            </div>
          </button>
        </Dropdown>
      </div>
    </header>
  );
}
